/**
 * 账单导入的**预览表**（D 域 · `DR-051` · 批 86）
 *
 * 依据：`D域PRD` **§5.2**（预览与逐行确认）· `AC-D1.1`（预览不落库）· `AC-D1.6`（建议 + 置信度）·
 * `AC-D2.1`（L1 不导入）· `AC-D2.3`（L2/L3 标注但**仍导入**）· `AC-D5.2`（逐行可改分类）·
 * `AC-D5.3`（未分类允许）
 *
 * ## 🔴 本组件**零判定**
 *
 * 是不是重复、建议哪个分类、置信度多高 —— 全是服务端字段，本组件只：
 * ① 显示；② 把用户的改动**回调上去**（`onChange`）。
 * 判重规则（L1 vs L2/L3 的差别）也只体现在**文案**上（`duplicateText()`），不在这里判断。
 *
 * ## 🔴 `disabled` 的语义
 *
 * 落库请求在飞时传 `true`：**行仍然渲染**（用户要能看见自己改了什么），
 * 只是控件不可点。若整块卸载，用户会以为"改的东西丢了"。
 */

import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import {
  CategoryKind,
  type CategoryView,
  type ImportPreviewResult,
  type ImportPreviewRow,
} from '@jiaying/shared';
import { Amount } from './Amount.js';
import { SearchSelect } from './SearchSelect.js';
import {
  categoryNameOf,
  confidenceBandLabel,
  duplicateText,
  eventLabel,
  importCategoryOptions,
  previewSummary,
  type ImportCategoryOption,
  type RowDecision,
} from '../features/import.js';

export interface ImportPreviewTableProps {
  readonly preview: ImportPreviewResult;
  /** 服务端下发的全量分类（本组件只用来出下拉与回显名字） */
  readonly categories: readonly CategoryView[];
  readonly decisions: ReadonlyMap<number, RowDecision>;
  readonly onChange: (gridRow: number, next: RowDecision) => void;
  readonly disabled?: boolean;
}

/** 单价行的分类方向：`out` ⇒ 支出分类、`in` ⇒ 收入分类（**由服务端解析出的方向决定**） */
function kindOf(row: ImportPreviewRow): CategoryKind {
  return row.direction === 'in' ? CategoryKind.Income : CategoryKind.Expense;
}

export function ImportPreviewTable({
  preview,
  categories,
  decisions,
  onChange,
  disabled = false,
}: ImportPreviewTableProps) {
  const expenseOptions = importCategoryOptions(categories, CategoryKind.Expense);
  const incomeOptions = importCategoryOptions(categories, CategoryKind.Income);

  return (
    <Box data-testid="import-preview">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="import-summary">
        {previewSummary(preview)}
        {preview.encoding !== 'utf-8' ? ` · 按 ${preview.encoding} 解码` : ''}
      </Typography>

      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
        {preview.rows.map((row) => {
          const dup = duplicateText(row.duplicateLevel);
          const decision = decisions.get(row.gridRow);
          const options: readonly ImportCategoryOption[] =
            kindOf(row) === CategoryKind.Income ? incomeOptions : expenseOptions;

          return (
            <Box
              key={row.gridRow}
              data-testid={`import-row-${String(row.gridRow)}`}
              // 🔴 不导入的行**变淡但不隐藏** —— 隐藏会让用户以为「账单少了行」
              sx={{ py: 1, opacity: row.willSkip ? 0.55 : 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary">
                  {row.date}
                  {row.timeText === null ? '' : ` ${row.timeText}`}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.merchant}
                </Typography>
                <Amount
                  variant="body2"
                  cents={row.amountCents}
                  currency={preview.currency}
                  colored={false}
                  signed
                />
                <Chip size="small" variant="outlined" label={eventLabel(row.event)} />
                {dup !== null && (
                  <Chip
                    size="small"
                    data-testid={`import-dup-${String(row.gridRow)}`}
                    color={row.willSkip ? 'default' : 'warning'}
                    variant={row.willSkip ? 'filled' : 'outlined'}
                    label={dup}
                  />
                )}
              </Stack>

              {/* 建议与置信度：低置信度**显式说出来**，不假装很确定 */}
              {!row.willSkip && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid={`import-suggest-${String(row.gridRow)}`}
                >
                  建议：{categoryNameOf(categories, row.suggestedCategoryId) ?? '未分类'}（置信度
                  {confidenceBandLabel(row.confidenceBand)}）
                  {row.conflictCategoryId === null
                    ? ''
                    : ` · 与已有规则不一致（规则指向「${categoryNameOf(categories, row.conflictCategoryId) ?? '未知'}」）`}
                </Typography>
              )}

              {!row.willSkip && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Box sx={{ minWidth: 220, flex: '0 1 320px' }}>
                    <SearchSelect<ImportCategoryOption>
                      label="分类"
                      options={options}
                      value={decision?.categoryId ?? ''}
                      onChange={(id) =>
                        onChange(row.gridRow, {
                          categoryId: id === '' ? null : id,
                          remember: decision?.remember ?? false,
                        })
                      }
                      groupBy={(o) => o.group}
                      disabled={disabled}
                      noOptionsText="没有匹配的分类 —— 可以先不选，稍后在流水里补"
                    />
                  </Box>
                  {/* 🔴 `data-testid` 挂在**外层 span**：MUI 6 的 Checkbox `inputProps`
                      类型不接受 `data-*`（对象字面量里 `data-*` 不合法），硬写会 TS2353 */}
                  <Box component="span" data-testid={`import-remember-${String(row.gridRow)}`}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={decision?.remember === true}
                          disabled={disabled || (decision?.categoryId ?? null) === null}
                          onChange={(e) =>
                            onChange(row.gridRow, {
                              categoryId: decision?.categoryId ?? null,
                              remember: e.target.checked,
                            })
                          }
                        />
                      }
                      label={<Typography variant="body2">记住这条规则</Typography>}
                    />
                  </Box>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>

      {preview.skipped.length > 0 && (
        <Box sx={{ mt: 1.5 }} data-testid="import-skipped">
          <Typography variant="body2" color="text.secondary">
            有 {preview.skipped.length} 行无法识别，已跳过：
          </Typography>
          {preview.skipped.slice(0, 5).map((s) => (
            <Typography key={s.gridRow} variant="caption" color="text.secondary" display="block">
              第 {s.gridRow + 1} 行：{s.reason}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
